import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  ClipboardCheck,
  Clock3,
  Eye,
  PencilLine,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  WifiOff,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/shadcn/ui/button";
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from "../../../components/workspaceSidebar";

import { closureSentence, patientName } from "../providerApi";
import type { DaySheet, FacilityScope, RosterPatient } from "../providerApi";
import type { FieldWorkspaceProps } from "./contracts";
import { Notice } from "./FieldPrimitives";
import FacilityPicker from "./FacilityPicker";
import { PODIATRY_VIEW_PARAM, isPodiatryView, podiatryPath } from "./practices";
import type { PodiatryView } from "./practices";
import Arrival from "./FieldArrival";
import {
  PRACTICE_STATUS_WORDS,
  STATUS_WORDS,
  UNREACHED_WORDS,
  clinicalApi,
  dayWords,
  isEncounter,
  startBlockedWords,
  unanswered,
  useActionKeys,
  useViewingAs,
  viewingWords,
} from "./clinical/clinicalApi";
import { practiceStatusOf, usePractices } from "./clinical/PracticeSwitcher";
import type { PracticesLoad } from "./clinical/PracticeSwitcher";
import type { EncounterStatus, PatientEncounters } from "./clinical/types";
import "./field-workspace.css";
import "./clinical/encounter-workbench.css";
import "./podiatry-workspace.css";

/** The sidebar names the practice, not just the Provider workspace (owner, 2026-11-05). */
const PODIATRY_FACE = { label: "Podiatry", sub: "Provider field workspace" };

/**
 * The Podiatry workspace (2026-10-30): when the practice in view is Podiatry the whole provider workspace is Podiatry,
 * with its own nav, header and copy. Since 2026-11-03 it sits in the same WorkspaceLayout sidebar as the other
 * workspaces, and its views are URLs (practices.ts podiatryPath), not state. Only practice-neutral field operations are shared with the other workspace: the
 * building picker and arrival. Every access answer is the server's (GET /clinical/practices, GET
 * /clinical/patients/:id/encounters); nothing here grants anything, and in a read-only "Sign in as" session nothing
 * here writes (the encounter page and the roster offer no write, and the server refuses one).
 */
const EncounterWorkbench = lazy(() => import("./clinical/EncounterWorkbench"));

const NAV: { key: PodiatryView; label: string; Icon: typeof Building2 }[] = [
  { key: "buildings", label: "Buildings", Icon: Building2 },
  { key: "residents", label: "Residents", Icon: Users },
];

export interface PodiatryWorkspaceProps {
  sheet: DaySheet | null;
  scope: FacilityScope;
  facilityId: string;
  loading: boolean;
  loadedAt: Date | null;
  offline: boolean;
  practicesLoad: PracticesLoad;
  onFacility: (id: string) => void;
  onRefresh: () => void;
  onCheckIn: FieldWorkspaceProps["onCheckIn"];
  feedback: React.ReactNode;
  /** The other practice workspaces the server answers ACTIVE for this provider (a dual-practice switch). */
  switchTo?: { label: string; onSwitch: () => void } | null;
  /** The building's load state from the shell (a refusal or a skeleton), shown while there is no day sheet. */
  stateBlock?: React.ReactNode;
}

type Open = { patientId: string; name: string; encounterId: string | null };

export default function PodiatryWorkspace(props: PodiatryWorkspaceProps) {
  const { sheet, scope, facilityId, loading, loadedAt, offline, practicesLoad, onFacility, onRefresh, feedback, switchTo } = props;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawView = params.get(PODIATRY_VIEW_PARAM);
  // The view is the address. Residents is the default (Buildings when no building is open); a word that is not a view
  // is the default and the address is corrected in place, so what the bar says and what the page shows agree.
  const view: PodiatryView = isPodiatryView(rawView) ? rawView : facilityId ? "residents" : "buildings";
  useEffect(() => {
    if (rawView !== null && !isPodiatryView(rawView)) navigate(podiatryPath(facilityId, view), { replace: true });
  }, [rawView, facilityId, view, navigate]);
  // Already on Residents (re-choosing the open building): replace, so Back does not step through a duplicate.
  const showResidents = () => navigate(podiatryPath(facilityId, "residents"), { replace: view === "residents" });
  const [open, setOpen] = useState<Open | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const viewing = useViewingAs();
  const current = scope.facilities.find((f) => f.id === facilityId);
  const here = practiceStatusOf(practicesLoad, "PODIATRY");
  // Choosing the building that is already open changes nothing to load: the shell would clear its sheet and navigate to the
  // same path, and no effect keyed on the (unchanged) building would ever load it again. So it only shows the residents.
  const pickBuilding = (id: string) => {
    if (id !== facilityId) onFacility(id);
    else showResidents();
  };

  if (open) {
    return (
      <div className="pw-encounter-shell">
        <Suspense fallback={<p role="status" className="fw-cx-pad fw-muted">Opening the encounter…</p>}>
          <EncounterWorkbench
            layout="page"
            closeLabel="Back to residents"
            encounterId={open.encounterId}
            patientName={open.name}
            facilityName={current?.name || sheet?.facility.name}
            onClose={() => {
              setOpen(null);
              setRefreshKey((k) => k + 1);
            }}
            onChanged={() => setRefreshKey((k) => k + 1)}
          />
        </Suspense>
      </div>
    );
  }

  const title = view === "buildings" ? "Your Podiatry buildings." : "Residents, foot by foot.";
  const subtitle =
    view === "buildings"
      ? "Choose where you are documenting today. Each building says whether Podiatry is active there for you."
      : "Each resident's Podiatry status at this building. Start, continue, review and sign encounters here.";

  const groups: SidebarGroup[] = [
    {
      key: "workspace",
      label: "Workspace",
      items: NAV.map(({ key, label, Icon }) => ({
        key,
        label,
        icon: Icon,
        href: podiatryPath(facilityId, key),
        active: view === key,
      })),
    },
  ];

  return (
    <div className="fw pw" data-practice="podiatry" data-testid="podiatry-workspace">
      {/* Inside a landmark (axe `region`) and ahead of the sidebar, so keyboard users can still skip it. */}
      <nav aria-label="Skip links" className="fw-skip-nav">
        <a className="fw-skip" href="#pw-main">
          Skip to Podiatry workspace
        </a>
      </nav>
      <WorkspaceLayout workspace="PROVIDER" navLabel="Podiatry workspace" face={PODIATRY_FACE} groups={groups}>
      <div className="fw-body">
        <header className="fw-topbar">
          <div>
            <span>eMerge / </span>
            <strong>Podiatry</strong>
            <span> / {NAV.find((n) => n.key === view)?.label}</span>
          </div>
          <div className="fw-topbar-right">
            <span className={`fw-live${offline ? " offline" : ""}`}>
              <span />
              {offline ? "Offline" : "Connected"}
            </span>
            {feedback}
          </div>
        </header>
        <main id="pw-main" className="fw-main" tabIndex={-1}>
          <div className="fw-page-heading">
            <div>
              <div className="wsb-title">
                <SidebarTrigger />
                <div>
                  <p className="fw-eyebrow">Podiatry · field documentation</p>
                  <h1>{title}</h1>
                </div>
              </div>
              <p>{subtitle}</p>
            </div>
            <div className="fw-heading-side">
              <div className="fw-facility">
                <span>Building</span>
                <FacilityPicker facilities={scope.facilities} value={facilityId} fallbackName={sheet?.facility.name} onChange={(id) => (id === facilityId ? showResidents() : onFacility(id))} />
              </div>
              {/* The dual-practice switch moved here from the old dark sidebar, where the Audiology workspace's switcher also is now. */}
              {switchTo && (
                <div className="fw-practice">
                  <span className="fw-practice-label">Practice</span>
                  <SwitchPractice switchTo={switchTo} />
                </div>
              )}
            </div>
          </div>
          <div className="fw-freshness">
            <span>
              {offline ? <WifiOff /> : <Clock3 />}
              {offline
                ? "Offline · showing the last loaded data. Saving is unavailable."
                : loading
                  ? "Refreshing building data…"
                  : loadedAt
                    ? `Loaded ${loadedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · refresh to check for changes`
                    : "Waiting for building data"}
            </span>
            <Button
              variant="ghost"
              onClick={() => {
                onRefresh();
                setRefreshKey((k) => k + 1);
              }}
              disabled={loading || offline}
            >
              <RefreshCw className={loading ? "fw-spin" : ""} />
              Refresh
            </Button>
          </div>
          {viewing && (
            <div className="fw-cx-viewing pw-viewing" role="note" data-testid="viewing-banner">
              <Eye aria-hidden="true" />
              <span>{viewingWords(viewing)}</span>
            </div>
          )}
          {current?.closure && <Notice>{closureSentence(current.closure)}.</Notice>}

          {view === "buildings" && (
            <BuildingGrid
              facilities={scope.facilities}
              selected={facilityId}
              onPick={pickBuilding}
            />
          )}

          {view === "residents" && (
            <>
              {facilityId && !sheet && props.stateBlock ? (
                props.stateBlock
              ) : !facilityId || !sheet ? (
                <div className="fw-empty">
                  <Building2 />
                  <h3>{facilityId ? "Loading the building…" : "Choose a building"}</h3>
                  <p>Pick the building you are documenting at to see its residents.</p>
                </div>
              ) : (
                <>
                  <Arrival
                    key={facilityId}
                    sheet={sheet}
                    onCheckIn={props.onCheckIn}
                    offline={offline}
                    intro="Record your arrival, then document each resident's Podiatry encounter."
                  />
                  {here !== null && here !== "ACTIVE" && (
                    <Notice>
                      Podiatry here: {PRACTICE_STATUS_WORDS[here]}. Existing encounters can still be opened; new ones cannot be started.
                    </Notice>
                  )}
                  <ResidentRoster
                    key={`${facilityId}:${refreshKey}`}
                    sheet={sheet}
                    facilityId={facilityId}
                    viewing={viewing}
                    offline={offline}
                    onOpen={setOpen}
                  />
                </>
              )}
            </>
          )}
          <div className="fw-scope">
            <ShieldCheck />
            <strong>Care, within your scope.</strong>
            <p>Buildings and residents come from your server-authorized assignments.</p>
          </div>
          <footer className="fw-footer">
            eMerge Podiatry <span>·</span> Building scope, care assignment and every Podiatry permission are enforced by the
            server.
          </footer>
        </main>
      </div>
      </WorkspaceLayout>
    </div>
  );
}

/** A simple, prominent switch to the provider's other practice workspace. */
function SwitchPractice({ switchTo }: { switchTo: { label: string; onSwitch: () => void } }) {
  const [shown, setShown] = useState(false);
  return shown ? (
    <div className="pw-switch">
      <Button type="button" variant="outline" className="pw-switch-btn" onClick={switchTo.onSwitch} data-testid="switch-practice-go">
        Open the {switchTo.label} workspace
      </Button>
      <Button type="button" variant="ghost" onClick={() => setShown(false)}>
        Stay in Podiatry
      </Button>
    </div>
  ) : (
    <Button type="button" variant="outline" className="pw-switch-btn" onClick={() => setShown(true)} data-testid="switch-practice">
      Switch practice
    </Button>
  );
}

/* ------------------------------------------------------------- buildings */

function BuildingGrid({ facilities, selected, onPick }: { facilities: FacilityScope["facilities"]; selected: string; onPick: (id: string) => void }) {
  if (!facilities.length)
    return (
      <div className="fw-empty">
        <Building2 />
        <h3>No buildings in your scope</h3>
        <p>eMerge has no building assigned to you yet.</p>
      </div>
    );
  return (
    <ul className="pw-buildings" aria-label="Your buildings">
      {facilities.map((f) => (
        <BuildingCard key={f.id} id={f.id} name={f.name} city={[f.city, f.state].filter(Boolean).join(", ")} selected={f.id === selected} onPick={onPick} />
      ))}
    </ul>
  );
}

function BuildingCard({ id, name, city, selected, onPick }: { id: string; name: string; city: string; selected: boolean; onPick: (id: string) => void }) {
  const load = usePractices(id);
  const status = practiceStatusOf(load, "PODIATRY");
  const words = load.state === "loading" ? "Checking Podiatry…" : status ? (status === "ACTIVE" ? "Podiatry active" : `Podiatry ${PRACTICE_STATUS_WORDS[status]}`) : "Podiatry status unavailable";
  const tone = status === "ACTIVE" ? "on" : load.state === "loading" ? "wait" : "off";
  return (
    <li>
      <button type="button" className={`pw-building${selected ? " selected" : ""}`} onClick={() => onPick(id)} aria-current={selected ? "true" : undefined}>
        <span className="pw-building-icon" aria-hidden="true">
          <Building2 />
        </span>
        <span className="pw-building-main">
          <strong>{name}</strong>
          <small>{city || "Location not recorded"}</small>
        </span>
        <span className={`pw-pill ${tone}`} data-testid="building-status">
          {words}
        </span>
      </button>
    </li>
  );
}

/* --------------------------------------------------------------- roster */

type RowStatus =
  | { state: "loading" }
  | { state: "ready"; page: PatientEncounters }
  | { state: "withheld" }
  | { state: "failed" };

type Filter = "all" | "todo" | "draft" | "ready" | "signed";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "todo", label: "Not started" },
  { key: "draft", label: "Draft" },
  { key: "ready", label: "Ready to sign" },
  { key: "signed", label: "Signed" },
];

const latestStatus = (page: PatientEncounters): EncounterStatus | null => page.encounters[0]?.status ?? null;
const filterOf = (s: RowStatus | undefined): Filter | null => {
  if (!s || s.state !== "ready") return null;
  const st = latestStatus(s.page);
  return st === "SIGNED" ? "signed" : st === "READY_FOR_SIGNATURE" ? "ready" : st === "DRAFT" ? "draft" : "todo";
};

function ResidentRoster({
  sheet,
  facilityId,
  viewing,
  offline,
  onOpen,
}: {
  sheet: DaySheet;
  facilityId: string;
  viewing: string | null;
  offline: boolean;
  onOpen: (o: Open) => void;
}) {
  const rows = sheet.roster.rows;
  const [statuses, setStatuses] = useState<Record<string, RowStatus>>({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    let live = true;
    const ids = rows.map((r) => r.id);
    // A few at a time: a building's roster never fires every read at once.
    let next = 0;
    const work = async () => {
      while (live && next < ids.length) {
        const id = ids[next++];
        let s: RowStatus;
        try {
          const r = await clinicalApi.patientEncounters(id, facilityId);
          if (r.success && r.data && Array.isArray(r.data.encounters)) s = { state: "ready", page: r.data };
          else if (!unanswered(r) && (r.status === 403 || r.status === 404)) s = { state: "withheld" };
          else s = { state: "failed" };
        } catch {
          s = { state: "failed" };
        }
        if (live) setStatuses((m) => ({ ...m, [id]: s }));
      }
    };
    Promise.all([work(), work(), work(), work()]);
    return () => {
      live = false;
    };
  }, [rows, facilityId]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: rows.length, todo: 0, draft: 0, ready: 0, signed: 0 };
    rows.forEach((r) => {
      const f = filterOf(statuses[r.id]);
      if (f) c[f] += 1;
    });
    return c;
  }, [rows, statuses]);

  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => {
    if (needle && !`${patientName(r)} ${r.room_number || ""}`.toLowerCase().includes(needle)) return false;
    return filter === "all" || filterOf(statuses[r.id]) === filter;
  });

  return (
    <section className="pw-roster" aria-labelledby="pw-roster-h">
      <div className="pw-roster-head">
        <div>
          <h2 id="pw-roster-h">Residents at {sheet.facility.name}</h2>
          <p className="fw-muted">
            {sheet.roster.returned} of {sheet.roster.total} residents on this page. Podiatry status is the server's, per resident.
          </p>
        </div>
        <label className="pw-search">
          <Search aria-hidden="true" />
          <span className="fw-cx-sr">Search residents</span>
          <input type="search" placeholder="Search by name or room" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      <div className="pw-filters" role="group" aria-label="Filter by Podiatry status">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" className={`pw-filter${filter === f.key ? " on" : ""}`} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label} <span>{counts[f.key]}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="fw-empty">
          <Users />
          <h3>{rows.length ? "No resident matches" : "No residents on this page"}</h3>
          <p>{rows.length ? "Clear the search or choose another status." : "eMerge lists no residents here for you."}</p>
        </div>
      ) : (
        <ul className="pw-rows" aria-label="Residents">
          {shown.map((r) => (
            <ResidentRow key={r.id} row={r} status={statuses[r.id]} facilityId={facilityId} viewing={viewing} offline={offline} onOpen={onOpen} />
          ))}
        </ul>
      )}
    </section>
  );
}

const TONE: Record<EncounterStatus, string> = { DRAFT: "draft", READY_FOR_SIGNATURE: "ready", SIGNED: "signed" };
const ICON: Record<EncounterStatus, typeof PencilLine> = { DRAFT: PencilLine, READY_FOR_SIGNATURE: ClipboardCheck, SIGNED: BadgeCheck };
const START_UNCONFIRMED = `${UNREACHED_WORDS} the start. Nothing was created that you can see. Try again; it will not be started twice.`;

function ResidentRow({
  row,
  status,
  facilityId,
  viewing,
  offline,
  onOpen,
}: {
  row: RosterPatient;
  status: RowStatus | undefined;
  facilityId: string;
  viewing: string | null;
  offline: boolean;
  onOpen: (o: Open) => void;
}) {
  const name = patientName(row);
  const { keyFor, settle } = useActionKeys();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const page = status?.state === "ready" ? status.page : null;
  const latest = page?.encounters[0] || null;
  const lastSigned = page?.encounters.find((e) => e.status === "SIGNED") || null;

  const start = useCallback(async () => {
    // Never while viewing: the button is not offered, and the server would refuse the POST anyway.
    if (starting || viewing) return;
    setStarting(true);
    setError("");
    const action = `create:${facilityId}:${row.id}`;
    try {
      const r = await clinicalApi.create(row.id, facilityId, keyFor(action));
      if (r.success && r.data && isEncounter(r.data.encounter)) {
        settle(action);
        onOpen({ patientId: row.id, name, encounterId: r.data.encounter.id });
      } else if (unanswered(r)) setError(START_UNCONFIRMED);
      else {
        settle(action);
        setError(r.code ? r.message || startBlockedWords(r.code) : r.status === 404 ? "Podiatry documentation is not open right now. Nothing was created." : r.message || "The encounter was not started.");
      }
    } catch {
      setError(START_UNCONFIRMED);
    } finally {
      setStarting(false);
    }
  }, [starting, viewing, facilityId, row.id, keyFor, settle, onOpen, name]);

  let badge: React.ReactNode;
  if (!status || status.state === "loading") badge = <span className="pw-pill wait">Checking…</span>;
  else if (status.state === "withheld") badge = <span className="pw-pill off">Not in your Podiatry care</span>;
  else if (status.state === "failed") badge = <span className="pw-pill off">Status not confirmed</span>;
  else if (!latest) badge = <span className="pw-pill todo">Not started</span>;
  else
    badge = (
      <span className={`fw-cx-status ${TONE[latest.status] || "draft"}`}>
        {React.createElement(ICON[latest.status] || PencilLine, { "aria-hidden": true })} {STATUS_WORDS[latest.status] || latest.status}
      </span>
    );

  let action: React.ReactNode = null;
  if (page) {
    const own = page.open_encounter_id;
    if (own && !viewing)
      action = (
        <Button type="button" className="fw-primary pw-act" onClick={() => onOpen({ patientId: row.id, name, encounterId: own })} aria-label={`Continue the Podiatry encounter of ${name}`}>
          <PencilLine aria-hidden="true" /> Continue
        </Button>
      );
    else if (latest)
      action = (
        <Button type="button" variant="outline" className="pw-act" onClick={() => onOpen({ patientId: row.id, name, encounterId: latest.id })} aria-label={`Open the latest Podiatry encounter of ${name}${viewing ? " (read-only)" : ""}`}>
          Open
        </Button>
      );
    if (!own && !viewing && page.can_start)
      action = (
        <>
          {action}
          <Button type="button" className="fw-primary pw-act" disabled={starting || offline} onClick={start} aria-label={`Start a Podiatry encounter for ${name}`}>
            {starting ? "Starting…" : "Start encounter"}
          </Button>
        </>
      );
    if (viewing && !latest)
      action = (
        <Button type="button" variant="outline" className="pw-act" onClick={() => onOpen({ patientId: row.id, name, encounterId: null })} aria-label={`View the Podiatry form for ${name} (read-only)`}>
          <Eye aria-hidden="true" /> View the form
        </Button>
      );
  }

  return (
    <li className="pw-row" data-testid="podiatry-resident">
      <div className="pw-row-who">
        <span className="pw-avatar" aria-hidden="true">
          {(row.first_name || "?").slice(0, 1)}
          {(row.last_name || "").slice(0, 1)}
        </span>
        <div>
          <strong>{name}</strong>
          <small>Room {row.room_number || "not recorded"}</small>
        </div>
      </div>
      <div className="pw-row-status">{badge}</div>
      <div className="pw-row-dates">
        <small>Last signed visit</small>
        <span>{lastSigned ? dayWords(lastSigned.encounter_date) : page ? "None yet" : "—"}</span>
      </div>
      <div className="pw-row-actions">
        {action}
        {page && !page.can_start && !page.open_encounter_id && !latest && !viewing && <small className="fw-muted">{startBlockedWords(page.start_blocked_reason)}</small>}
        {error && (
          <p role="alert" className="fw-cx-error">
            {error}
          </p>
        )}
      </div>
    </li>
  );
}
